import { useApi } from '@hooks/useApi';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { queryKeys } from '@utils/consts';
import { ApiError, MerchantUser } from '@root/globalTypes';
import { isError } from '@utils/apiHelpers';
import { useAppContext } from '@hooks/useAppContext';
import { UpdateMerchantUserData } from '@services/types';

type UpdateUserParams = {
  id: string;
  callback: (data: MerchantUser) => void;
};

export function useUpdateUser({ id, callback }: UpdateUserParams) {
  const { query } = useApi();
  const queryClient = useQueryClient();
  const { user } = useAppContext();
  const [apiError, setApiError] = useState<ApiError | null>(null);

  const { mutate, isPending } = useMutation({
    mutationFn: ({ ...data }: UpdateMerchantUserData): Promise<MerchantUser | ApiError> => {
      return query({
        path: `v1/merchant/users/${id}`,
        data: data,
        opt: {
          method: 'PUT',
        },
      });
    },
    onSettled: (data) => {
      if (!data) return;
      if (isError(data)) {
        setApiError(data);
      } else {
        callback(data);
      }
    },
    onSuccess: async (data) => {
      await queryClient.refetchQueries({ queryKey: queryKeys.merchantUsers });
      if (isError(data)) return;
      if (user?.id === data.id) {
        await queryClient.refetchQueries({ queryKey: queryKeys.loggedUser });
      }
    },
  });

  return {
    mutate,
    isPending,
    error: apiError,
  };
}
